import React, { useState, useEffect, useCallback, useRef } from "react";
import DeltaBadge from "../components/ui/DeltaBadge";

const API = "https://ceo-cockpit-3npo.onrender.com";

const YEARS    = ["FY22","FY23","FY24","FY25","FY26","FY27"];
const MONTHS   = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];
const QUARTERS = ["Q1","Q2","Q3","Q4"];
const HALVES   = ["H1","H2"];

function toDbMonth(fy: string, m: string): string {
  const n   = parseInt(fy.slice(2));
  const h1  = String(n - 1);
  const h2b = String(n);
  const h1m  = ["Apr","May","Jun","Jul","Aug","Sep"];
  const h2am = ["Oct","Nov","Dec"];
  if (h1m.includes(m) || h2am.includes(m)) return `${m}-${h1}`;
  return `${m}-${h2b}`;
}

function qMonths(q: string): string[] {
  const map: Record<string,string[]> = {
    Q1:["Apr","May","Jun"], Q2:["Jul","Aug","Sep"],
    Q3:["Oct","Nov","Dec"], Q4:["Jan","Feb","Mar"],
  };
  return map[q] || [];
}

function toDbParam(period: string, fy: string): string {
  if (period === "Annual") return "Annual";
  if (period === "YTD")    return "Annual";
  if (period === "H1") return ["Apr","May","Jun","Jul","Aug","Sep"].map(m=>toDbMonth(fy,m)).join(",");
  if (period === "H2") return ["Oct","Nov","Dec","Jan","Feb","Mar"].map(m=>toDbMonth(fy,m)).join(",");
  if (["Q1","Q2","Q3","Q4"].includes(period)) return qMonths(period).map(m=>toDbMonth(fy,m)).join(",");
  return toDbMonth(fy, period);
}

function periodOptions(mode: string): string[] {
  if (mode === "QoQ")      return QUARTERS;
  if (mode === "BiYearly") return HALVES;
  if (mode === "YoY")      return [];
  return ["YTD", ...MONTHS];
}

// Selection item: one year + one period
interface SelItem { year: string; period: string; }
function selKey(s: SelItem) { return `${s.year}||${s.period}`; }

interface PnLRow {
  row: number; label: string; type: "absolute"|"percent";
  is_formula: boolean; display_as_cost: boolean;
  signed: boolean; bold: boolean;
  selected_value: number|null; compare_value: number|null;
  delta: number|string|null;
}

const INDENT: Record<number,number> = {
  6:1,7:1,9:1,11:1,12:1,
  20:1,21:1,23:1,24:1,25:1,26:1,27:1,28:1,
  30:1,31:1,32:1,33:1,34:1,
  36:1,37:1,38:1,39:1,40:1,41:1,42:1,43:1,
  45:1,46:1,47:1,48:1,49:1,50:1,51:1,
  56:1,57:1,61:1,62:1,63:1,64:1,65:1,66:1,67:1,
};

function fmtVal(val: number|null, type: string, isCost: boolean, signed: boolean): string {
  if (val === null || val === undefined) return "--";
  if (type === "percent") return `${val.toFixed(2)}%`;
  const abs = Math.abs(val);
  const f   = abs.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
  if (signed) return val < 0 ? `(${Math.abs(val).toFixed(2)})` : val.toFixed(2);
  if (isCost) return `(${f})`;
  return val < 0 ? `(${f})` : f;
}

function valColor(val: number|null, isCost: boolean, signed: boolean): string {
  if (val === null) return "var(--text-muted)";
  if (signed) return val < 0 ? "var(--delta-negative)" : "var(--text-number)";
  if (isCost) return "var(--delta-negative)";
  if (val < 0) return "var(--delta-negative)";
  return "var(--text-number)";
}

// ── Mode dropdown ─────────────────────────────────────────────────────────────
function ModeDropdown({ value, onChange }: { value: string; onChange: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const modes = ["MoM","QoQ","BiYearly","YoY"];
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{position:"relative",display:"inline-block"}}>
      <button onClick={()=>setOpen(p=>!p)} style={{
        padding:"6px 12px",borderRadius:"6px",border:"1px solid var(--border)",
        backgroundColor:"var(--bg-secondary)",color:"var(--text-primary)",
        fontSize:"12px",fontWeight:600,cursor:"pointer",
        display:"flex",alignItems:"center",gap:"8px",minWidth:"130px",justifyContent:"space-between",
      }}>
        <span>Mode: {value}</span>
        <span style={{fontSize:"10px"}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div style={{
          position:"absolute",top:"calc(100% + 4px)",left:0,
          backgroundColor:"var(--bg-primary)",border:"1px solid var(--border)",
          borderRadius:"8px",zIndex:999,minWidth:"140px",
          boxShadow:"0 8px 24px rgba(0,0,0,0.15)",overflow:"hidden",
        }}>
          {modes.map(m=>(
            <div key={m} onClick={()=>{onChange(m);setOpen(false);}} style={{
              padding:"8px 16px",cursor:"pointer",fontSize:"12px",
              fontWeight:m===value?600:400,
              color:m===value?"var(--accent-gold)":"var(--text-primary)",
              backgroundColor:m===value?"rgba(201,168,76,0.1)":"transparent",
              borderBottom:"1px solid var(--border)",
              display:"flex",alignItems:"center",gap:"8px",
            }}
            onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="var(--bg-secondary)"}
            onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=m===value?"rgba(201,168,76,0.1)":"transparent"}
            >
              <span>{m===value?"●":"○"}</span>{m}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Multi-year Period Dropdown ────────────────────────────────────────────────
// Allows selecting periods from multiple years simultaneously
interface MultiYearDropdownProps {
  label:      string;
  mode:       string;
  selections: SelItem[];
  onToggle:   (item: SelItem) => void;
}

function MultiYearDropdown({ label, mode, selections, onToggle }: MultiYearDropdownProps) {
  const [open,     setOpen]     = useState(false);
  const [expanded, setExpanded] = useState<Record<string,boolean>>({});
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const opts = periodOptions(mode);
  const selKeys = new Set(selections.map(selKey));

  // Display label
  const displayLabel = selections.length === 0
    ? "Select..."
    : selections.length === 1
      ? mode==="YoY" ? selections[0].year : `${selections[0].period} ${selections[0].year}`
      : `${selections.length} selected`;

  function toggleExpand(fy: string) {
    setExpanded(prev => ({...prev, [fy]: !prev[fy]}));
  }

  return (
    <div ref={ref} style={{position:"relative",display:"inline-block"}}>
      <button onClick={()=>setOpen(p=>!p)} style={{
        padding:"6px 12px",borderRadius:"6px",
        border:"1px solid var(--accent-gold)",
        backgroundColor:"transparent",color:"var(--accent-gold)",
        fontSize:"12px",fontWeight:600,cursor:"pointer",
        display:"flex",alignItems:"center",gap:"6px",
        minWidth:"160px",justifyContent:"space-between",
      }}>
        <span>{label}: {displayLabel}</span>
        <span style={{fontSize:"10px"}}>{open?"▲":"▼"}</span>
      </button>

      {open && (
        <div style={{
          position:"absolute",top:"calc(100% + 4px)",left:0,
          backgroundColor:"var(--bg-primary)",border:"1px solid var(--border)",
          borderRadius:"8px",zIndex:999,minWidth:"220px",maxHeight:"360px",
          overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,0.15)",
        }}>
          {YEARS.map(fy=>{
            const isExp = expanded[fy] ?? false;
            // For YoY — year itself is the selectable item
            if (mode === "YoY") {
              const item = {year:fy, period:"Annual"};
              const isOn = selKeys.has(selKey(item));
              return (
                <div key={fy} onClick={()=>onToggle(item)} style={{
                  padding:"8px 16px",cursor:"pointer",
                  display:"flex",alignItems:"center",gap:"8px",
                  fontSize:"12px",fontWeight:isOn?600:400,
                  color:isOn?"var(--accent-gold)":"var(--text-primary)",
                  backgroundColor:isOn?"rgba(201,168,76,0.1)":"transparent",
                  borderBottom:"1px solid var(--border)",
                }}
                onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="var(--bg-secondary)"}
                onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=isOn?"rgba(201,168,76,0.1)":"transparent"}
                >
                  <input type="checkbox" checked={isOn} readOnly
                    style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px",pointerEvents:"none"}}/>
                  {fy}
                </div>
              );
            }

            // For MoM/QoQ/BiYearly — year expands to show periods
            const anySelected = opts.some(p => selKeys.has(selKey({year:fy,period:p})));
            return (
              <div key={fy}>
                {/* Year row */}
                <div onClick={()=>toggleExpand(fy)} style={{
                  padding:"8px 12px",cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  backgroundColor:anySelected?"rgba(201,168,76,0.06)":"transparent",
                  borderBottom:"1px solid var(--border)",
                  fontSize:"12px",fontWeight:anySelected?600:400,
                  color:anySelected?"var(--accent-gold)":"var(--text-primary)",
                }}
                onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="var(--bg-secondary)"}
                onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=anySelected?"rgba(201,168,76,0.06)":"transparent"}
                >
                  <span>{fy} {anySelected && `(${opts.filter(p=>selKeys.has(selKey({year:fy,period:p}))).length})`}</span>
                  <span style={{fontSize:"10px"}}>{isExp?"▼":"▶"}</span>
                </div>

                {/* Period sub-rows */}
                {isExp && (
                  <div style={{backgroundColor:"var(--bg-secondary)"}}>
                    {opts.map(p=>{
                      const item = {year:fy, period:p};
                      const isOn = selKeys.has(selKey(item));
                      return (
                        <div key={p} onClick={()=>onToggle(item)} style={{
                          padding:"6px 12px 6px 28px",cursor:"pointer",
                          display:"flex",alignItems:"center",gap:"8px",
                          fontSize:"12px",
                          color:isOn?"var(--accent-gold)":"var(--text-secondary)",
                          backgroundColor:isOn?"rgba(201,168,76,0.08)":"transparent",
                          borderBottom:"1px solid var(--border)",
                        }}
                        onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor="rgba(201,168,76,0.05)"}
                        onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.backgroundColor=isOn?"rgba(201,168,76,0.08)":"transparent"}
                        >
                          <input type="checkbox" checked={isOn} readOnly
                            style={{accentColor:"var(--accent-gold)",width:"13px",height:"13px",pointerEvents:"none"}}/>
                          {p}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ on, onToggle, label }: { on:boolean; onToggle:()=>void; label:string }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:"8px",cursor:"pointer"}} onClick={onToggle}>
      <div style={{
        width:"34px",height:"18px",borderRadius:"9px",
        backgroundColor:on?"var(--accent-gold)":"var(--border)",
        position:"relative",transition:"background-color 0.2s",flexShrink:0,
      }}>
        <div style={{
          position:"absolute",top:"2px",left:on?"18px":"2px",
          width:"14px",height:"14px",borderRadius:"50%",backgroundColor:"#fff",
          transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)",
        }}/>
      </div>
      <span style={{fontSize:"11px",color:"var(--text-secondary)",fontWeight:500}}>{label}</span>
    </div>
  );
}

// ── Main MIS ─────────────────────────────────────────────────────────────────
export default function MIS() {
  const [mode,     setMode]     = useState("MoM");
  const [baseSels, setBaseSels] = useState<SelItem[]>([{year:"FY27",period:"Apr"}]);
  const [deltaOn,  setDeltaOn]  = useState(true);
  const [cmpSels,  setCmpSels]  = useState<SelItem[]>([{year:"FY26",period:"Apr"}]);
  const [mirrorCmp,setMirrorCmp]= useState(true);

  // colData key = selKey(item), value = rows from API
  const [colData, setColData] = useState<Record<string, PnLRow[]>>({});
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string|null>(null);

  // Reset on mode change
  useEffect(() => {
    const def = mode==="QoQ"?"Q1":mode==="BiYearly"?"H1":mode==="YoY"?"Annual":"Apr";
    const baseY = "FY27"; const cmpY = "FY26";
    setBaseSels([{year:baseY,period:def}]);
    setCmpSels([{year:cmpY,period:def}]);
    setMirrorCmp(true);
  }, [mode]);

  // Mirror compare selections
  useEffect(() => {
    if (!mirrorCmp) return;
    // Mirror: for each base selection, find the equivalent in cmp year
    // Use one year back from each base year
    const mirrored = baseSels.map(s => {
      const n = parseInt(s.year.slice(2));
      return {year:`FY${n-1}`, period:s.period};
    });
    setCmpSels(mirrored);
  }, [baseSels, mirrorCmp]);

  function toggleBase(item: SelItem) {
    const k = selKey(item);
    setBaseSels(prev => {
      const exists = prev.some(s=>selKey(s)===k);
      if (exists) return prev.length>1 ? prev.filter(s=>selKey(s)!==k) : prev;
      return [...prev, item];
    });
  }

  function toggleCmp(item: SelItem) {
    setMirrorCmp(false);
    const k = selKey(item);
    setCmpSels(prev => {
      const exists = prev.some(s=>selKey(s)===k);
      if (exists) return prev.length>1 ? prev.filter(s=>selKey(s)!==k) : prev;
      return [...prev, item];
    });
  }

  const fetchAll = useCallback(async () => {
    if (baseSels.length === 0) return;
    setLoading(true); setError(null);
    try {
      const results: Record<string, PnLRow[]> = {};
      // Fetch base columns
      for (const item of baseSels) {
        const bDb = toDbParam(item.period, item.year);
        // Find matching compare item
        const cmpItem = cmpSels[baseSels.indexOf(item)] || cmpSels[0];
        const cDb = cmpItem ? toDbParam(cmpItem.period, cmpItem.year) : bDb;
        const cmpYear = cmpItem ? cmpItem.year : item.year;
        const url = `${API}/monthly-pnl?fiscal_year=${item.year}&months=${encodeURIComponent(bDb)}&compare_year=${cmpYear}&compare_months=${encodeURIComponent(cDb)}&delta=${deltaOn}`;
        const res  = await fetch(url);
        if (!res.ok) throw new Error("API error");
        const json = await res.json();
        results[selKey(item)] = json.rows || [];
      }
      setColData(results);
    } catch {
      setError("Failed to load data. Is the API running?");
    } finally {
      setLoading(false);
    }
  }, [baseSels, cmpSels, deltaOn]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const firstKey     = baseSels.length > 0 ? selKey(baseSels[0]) : "";
  const templateRows = colData[firstKey] || [];
  const COL_W        = 100;
  const showTotal    = !deltaOn && baseSels.length > 1;

  // Calculate total for a row across all selected columns
  function calcTotal(rowNum: number, field: "selected_value"): number|null {
    const vals = baseSels.map(item => {
      const rows = colData[selKey(item)] || [];
      return rows.find(r=>r.row===rowNum)?.[field] ?? null;
    });
    if (vals.every(v=>v===null)) return null;
    return vals.reduce((sum, v) => (sum||0) + (v||0), 0);
  }

  // For % rows: recalculate from summed absolute values
  const PCT_PAIRS: Record<number, [number, number]> = {
    7:  [6,  5],  // Discounts% = Discounts / Gross Revenue
    17: [16, 10], // GM% = Gross Profit / Net Revenue
    54: [53, 10], // EBITDA% = EBITDA / Net Revenue
  };

  function calcPctTotal(rowNum: number): number|null {
    const pair = PCT_PAIRS[rowNum];
    if (!pair) return calcTotal(rowNum, "selected_value");
    const [numRow, denRow] = pair;
    const numTotal = calcTotal(numRow, "selected_value");
    const denTotal = calcTotal(denRow, "selected_value");
    if (!denTotal || denTotal === 0) return null;
    return ((numTotal||0) / denTotal) * 100;
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:"16px"}}>
        <h1 style={{fontSize:"18px",fontWeight:700,color:"var(--text-primary)",margin:0}}>MIS</h1>
        <p style={{fontSize:"12px",color:"var(--text-secondary)",margin:"2px 0 0"}}>Monthly Management Information System - Rs. Lakhs</p>
      </div>

      {/* Filter Panel */}
      <div style={{backgroundColor:"var(--bg-secondary)",border:"1px solid var(--border)",borderRadius:"8px",padding:"14px 16px",marginBottom:"16px"}}>

        {/* Row 1: Mode + View dropdown */}
        <div style={{display:"flex",gap:"12px",alignItems:"center",marginBottom:"12px",flexWrap:"wrap"}}>
          <ModeDropdown value={mode} onChange={setMode}/>
          <MultiYearDropdown
            label="View" mode={mode}
            selections={baseSels} onToggle={toggleBase}
          />
        </div>

        {/* Divider */}
        <div style={{borderTop:"1px solid var(--border)",margin:"8px 0"}}/>

        {/* Row 2: Delta toggle + compare */}
        <div style={{display:"flex",gap:"12px",alignItems:"center",flexWrap:"wrap"}}>
          <Toggle on={deltaOn} onToggle={()=>setDeltaOn(p=>!p)} label="Show Delta"/>
          {deltaOn && (
            <>
              <span style={{fontSize:"11px",color:"var(--text-muted)",fontWeight:600}}>VS</span>
              <MultiYearDropdown
                label="Compare" mode={mode}
                selections={cmpSels} onToggle={toggleCmp}
              />
              <label style={{display:"flex",alignItems:"center",gap:"6px",cursor:"pointer",marginLeft:"auto"}}>
                <input type="checkbox" checked={mirrorCmp}
                  onChange={e=>{setMirrorCmp(e.target.checked);}}
                  style={{accentColor:"var(--accent-gold)"}}/>
                <span style={{fontSize:"11px",color:"var(--text-secondary)"}}>Mirror base period</span>
              </label>
            </>
          )}
        </div>
      </div>

      {/* States */}
      {loading && <div style={{textAlign:"center",padding:"60px",color:"var(--text-muted)"}}>Loading data...</div>}
      {error   && <div style={{padding:"16px",backgroundColor:"var(--bg-secondary)",border:"1px solid var(--border)",borderRadius:"8px",color:"var(--text-secondary)",fontSize:"13px"}}>{error}</div>}

      {/* Table */}
      {!loading && !error && templateRows.length > 0 && (
        <div style={{border:"1px solid var(--border)",borderRadius:"8px",overflow:"hidden"}}>
          <div style={{overflowX:"auto"}}>
            <div style={{minWidth:`${240 + baseSels.length*(deltaOn?COL_W*3:COL_W) + (showTotal?COL_W:0)}px`}}>

              {/* Header */}
              <div style={{display:"flex",backgroundColor:"var(--bg-header)",position:"sticky",top:0,zIndex:2}}>
                {/* Frozen label */}
                <div style={{
                  width:240,minWidth:240,padding:"10px 12px",
                  fontSize:"11px",fontWeight:600,color:"var(--text-header)",
                  position:"sticky",left:0,backgroundColor:"var(--bg-header)",zIndex:3,
                  borderRight:"1px solid var(--border-sidebar)",
                }}>
                  Particulars - Rs. Lakhs
                </div>

                {/* One group per base selection */}
                {baseSels.map(item=>{
                  const cmpItem = cmpSels[baseSels.indexOf(item)] || cmpSels[0];
                  const colLabel = item.period==="Annual" ? item.year : `${item.period} ${item.year}`;
                  const cmpLabel = cmpItem
                    ? (cmpItem.period==="Annual" ? cmpItem.year : `${cmpItem.period} ${cmpItem.year}`)
                    : "";
                  return (
                    <div key={selKey(item)} style={{display:"flex",borderLeft:"2px solid var(--border-sidebar)"}}>
                      <div style={{width:COL_W,minWidth:COL_W,padding:"10px 8px",fontSize:"10px",fontWeight:600,color:"var(--accent-gold)",textAlign:"right",borderBottom:"2px solid var(--accent-gold)"}}>
                        {colLabel}
                      </div>
                      {deltaOn && <div style={{width:COL_W,minWidth:COL_W,padding:"10px 8px",fontSize:"10px",fontWeight:600,color:"#64748B",textAlign:"right",borderBottom:"2px solid #334155"}}>
                        {cmpLabel}
                      </div>}
                      {deltaOn && <div style={{width:COL_W,minWidth:COL_W,padding:"10px 8px",fontSize:"10px",fontWeight:600,color:"#64748B",textAlign:"right",borderBottom:"2px solid #334155"}}>
                        Delta
                      </div>}
                    </div>
                  );
                })}

                {/* Total header */}
                {showTotal && (
                  <div style={{width:COL_W,minWidth:COL_W,padding:"10px 8px",fontSize:"10px",fontWeight:700,color:"#FFFFFF",textAlign:"right",borderLeft:"2px solid var(--accent-gold)",borderBottom:"2px solid var(--accent-gold)"}}>
                    TOTAL
                  </div>
                )}
              </div>

              {/* Data rows */}
              {templateRows.map((row,idx)=>{
                const indent = INDENT[row.row]??0;
                const isAlt  = idx%2===1;
                const isGrowthRow = row.row >= 991;
                const totalVal = showTotal && !isGrowthRow
                  ? row.type==="percent" ? calcPctTotal(row.row) : calcTotal(row.row,"selected_value")
                  : null;
                const prevRow = templateRows[idx-1];
                const showGrowthHeader = isGrowthRow && (!prevRow || prevRow.row < 991);
                return (
                  <React.Fragment key={row.row}>
                    {showGrowthHeader && (
                      <div style={{display:"flex",backgroundColor:"rgba(201,168,76,0.08)",borderTop:"2px solid var(--accent-gold)"}}>
                        <div style={{width:"100%",padding:"6px 12px",fontSize:"11px",fontWeight:700,color:"var(--accent-gold)",fontStyle:"italic"}}>
                          YoY Growth % (full fiscal years only)
                        </div>
                      </div>
                    )}
                    <div style={{
                      display:"flex",
                      backgroundColor:isAlt?"var(--bg-row-alt)":"var(--bg-primary)",
                      borderTop:"1px solid var(--border)",
                    }}>
                      <div style={{
                        width:240,minWidth:240,padding:"5px 12px",
                        fontSize:row.bold?"12px":"11px",fontWeight:row.bold?700:400,
                        color:row.bold?"var(--text-primary)":"var(--text-secondary)",
                        paddingLeft:`${12+indent*14}px`,
                        position:"sticky",left:0,
                        backgroundColor:isAlt?"var(--bg-row-alt)":"var(--bg-primary)",
                        zIndex:1,borderRight:"1px solid var(--border)",
                      }}>
                        {row.label}
                      </div>
                      {baseSels.map(item=>{
                        const r = (colData[selKey(item)]||[]).find(x=>x.row===row.row);
                        if (isGrowthRow) {
                          const growthVal = r?.selected_value ?? null;
                          const colSpan = deltaOn ? 3 : 1;
                          return (
                            <div key={selKey(item)} style={{display:"flex",borderLeft:"2px solid var(--border)",width:COL_W*colSpan,minWidth:COL_W*colSpan}}>
                              <div className="num" style={{width:"100%",padding:"5px 8px",fontSize:"11px",textAlign:"right",
                                color:growthVal===null?"var(--text-muted)":growthVal<0?"var(--delta-negative)":"var(--delta-positive)"}}>
                                {growthVal!==null ? `${growthVal.toFixed(2)}%` : "--"}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={selKey(item)} style={{display:"flex",borderLeft:"2px solid var(--border)"}}>
                            <div className="num" style={{width:COL_W,minWidth:COL_W,padding:"5px 8px",fontSize:"11px",fontWeight:row.bold?600:400,color:valColor(r?.selected_value??null,row.display_as_cost,row.signed),textAlign:"right"}}>
                              {fmtVal(r?.selected_value??null,row.type,row.display_as_cost,row.signed)}
                            </div>
                            {deltaOn && <div className="num" style={{width:COL_W,minWidth:COL_W,padding:"5px 8px",fontSize:"11px",color:"var(--text-muted)",textAlign:"right"}}>
                              {fmtVal(r?.compare_value??null,row.type,row.display_as_cost,row.signed)}
                            </div>}
                            {deltaOn && <div style={{width:COL_W,minWidth:COL_W,padding:"5px 8px",textAlign:"right"}}>
                              <DeltaBadge value={r?.delta??null} rowType={row.type} isCost={row.display_as_cost}/>
                            </div>}
                          </div>
                        );
                      })}
                      {showTotal && !isGrowthRow && (
                        <div className="num" style={{
                          width:COL_W,minWidth:COL_W,padding:"5px 8px",
                          fontSize:"11px",fontWeight:row.bold?700:500,
                          color:valColor(totalVal,row.display_as_cost,row.signed),
                          textAlign:"right",
                          borderLeft:"2px solid var(--accent-gold)",
                          backgroundColor:"rgba(201,168,76,0.04)",
                        }}>
                          {totalVal!==null
                            ? row.type==="percent"
                              ? `${totalVal.toFixed(2)}%`
                              : fmtVal(totalVal,row.type,row.display_as_cost,row.signed)
                            : "--"}
                        </div>
                      )}
                      {showTotal && isGrowthRow && (
                        <div style={{width:COL_W,minWidth:COL_W,borderLeft:"2px solid var(--accent-gold)",backgroundColor:"rgba(201,168,76,0.04)"}}/>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
